import {
  type ColumnDef,
  type ColumnFiltersState,
  createColumnHelper,
  getCoreRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { useEffect, useMemo, useState } from 'react'

import { SortableHeader } from 'components/reusable/table/SortableHeader'
import TanstackTable, { type PaginationMetadata } from 'components/reusable/table/TanstackTable'
import { TextFilter } from 'components/reusable/table/TextFilter'
import { brokersMembers } from 'rails_routes'
import {
  getJsonUrlParamFromWindow,
  getNumberUrlParamFromWindow,
  setUrlParamOnWindow,
} from 'utils/url_params'
import { useGetBrokersMembers } from 'utils/web_api'
import { type BrokersBrokerMember, type GetBrokersMembersParams } from 'utils/web_api.schemas'
import { useWindowLocation } from 'utils/windowLocation'

const PAGE_SIZE = 20

const columnHelper = createColumnHelper<BrokersBrokerMember>()

function buildApiQueryParams ({
  columnFilters,
  pageIndex,
  pageSize,
  sorting,
}: {
  columnFilters: ColumnFiltersState,
  pageIndex: number,
  pageSize: number,
  sorting: SortingState
}): GetBrokersMembersParams {
  const params: GetBrokersMembersParams = {
    'page[number]': pageIndex,
    'page[size]': pageSize,
  }

  // Convert filters to API format
  columnFilters.forEach(filter => {
    const value = String(filter.value)
    if (value) {
      switch (filter.id) {
        case 'date_of_birth':
          params['filter[date_of_birth]'] = value
          break
        case 'email':
          params['filter[email]'] = value
          break
        case 'employer_name':
          params['filter[employer_name]'] = value
          break
        case 'family_name':
          params['filter[family_name]'] = value
          break
        case 'given_name':
          params['filter[given_name]'] = value
          break
        case 'id':
          params['filter[id]'] = value
          break
        case 'state':
          params['filter[state]'] = value
          break
      }
    }
  })

  // Convert sorting to API format
  if (sorting.length > 0) {
    const sort = sorting[0]
    const direction = sort.desc ? 'desc' : 'asc'

    switch (sort.id) {
      case 'date_of_birth':
        params['sort[date_of_birth]'] = direction
        break
      case 'employer_name':
        params['sort[employer_name]'] = direction
        break
      case 'family_name':
        params['sort[family_name]'] = direction
        break
      case 'given_name':
        params['sort[given_name]'] = direction
        break
      case 'state':
        params['sort[state]'] = direction
        break
    }
  }

  return params
}

function createColumns (): Array<ColumnDef<BrokersBrokerMember>> {
  return [
    columnHelper.accessor('given_name', {
      header: (info) => (
        <SortableHeader<BrokersBrokerMember> {...info} headerText='First Name' />
      ),
      cell: ({ getValue }) => {
        return (
          <div className='text-gray-900'>
            {getValue()}
          </div>
        )
      },
    }),
    columnHelper.accessor('family_name', {
      header: (info) => (
        <SortableHeader<BrokersBrokerMember> {...info} headerText='Last Name' />
      ),
      cell: ({ getValue }) => {
        return (
          <div className='text-gray-900'>
            {getValue()}
          </div>
        )
      },
    }),
    columnHelper.accessor((row) => row.employer_name ?? '', {
      id: 'employer_name',
      header: (info) => (
        <SortableHeader<BrokersBrokerMember> {...info} headerText='Employer' />
      ),
      cell: ({ getValue }) => {
        return (
          <div className='text-gray-900'>
            {getValue()}
          </div>
        )
      },
    }),
    columnHelper.accessor((row) => row.state ?? '', {
      id: 'state',
      header: (info) => (
        <SortableHeader<BrokersBrokerMember> {...info} headerText='State' />
      ),
      cell: ({ getValue }) => {
        return (
          <div className='text-gray-900'>
            {getValue()}
          </div>
        )
      },
    }),
    columnHelper.accessor((row) => row.date_of_birth ?? '', {
      id: 'date_of_birth',
      header: (info) => (
        <SortableHeader<BrokersBrokerMember> {...info} headerText='Date of Birth' />
      ),
      cell: ({ getValue }) => {
        return (
          <div className='text-gray-900'>
            {getValue()}
          </div>
        )
      },
    }),
  ]
}

export function BrokersMembersTable () {
  const { assign } = useWindowLocation()

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(getJsonUrlParamFromWindow('filters') ?? [])
  const [sorting, setSorting] = useState<SortingState>(getJsonUrlParamFromWindow('sort') ?? [])
  const [paginationState, setPaginationState] = useState({
    pageIndex: getNumberUrlParamFromWindow('pageIndex') ?? 1,
    pageSize: PAGE_SIZE,
  })

  // Convert TanStack state to Orval params format
  const apiQueryParams = useMemo(
    () => buildApiQueryParams({
      columnFilters,
      pageIndex: paginationState.pageIndex,
      pageSize: paginationState.pageSize,
      sorting,
    }),
    [
      columnFilters,
      paginationState.pageIndex,
      paginationState.pageSize,
      sorting,
    ]
  )

  const { data: apiData, isLoading, isError } = useGetBrokersMembers(apiQueryParams)

  const data = apiData?.data ?? []
  const paginationMetadata: PaginationMetadata | undefined = apiData?.pagination
    ? {
        total_records: apiData.pagination.total_records,
        current_page: apiData.pagination.current_page,
        total_pages: apiData.pagination.total_pages,
        next_page: apiData.pagination.next_page,
        prev_page: apiData.pagination.prev_page,
      }
    : undefined

  // Persist table pagination/filter/sort state in URL.
  // NOTE: Please take into consideration that storing state in the URL
  // sometimes results in embedding sensitive information in it (e.g.
  // email address, etc.).
  useEffect(() => {
    setUrlParamOnWindow('filters', columnFilters)
  }, [columnFilters])

  useEffect(() => {
    setUrlParamOnWindow('sort', sorting)
  }, [sorting])

  useEffect(() => {
    setUrlParamOnWindow('pageIndex', paginationState.pageIndex)
  }, [paginationState.pageIndex])

  const columns = useMemo(() => createColumns(), [])
  const table = useReactTable<BrokersBrokerMember>({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    pageCount: paginationMetadata?.total_pages ?? 0,
    state: {
      columnFilters,
      sorting,
      pagination: {
        // We use 1-based indexing in our pagination state, so we need to adjust to 0-based for TanStack's table
        pageIndex: paginationState.pageIndex - 1,
        pageSize: paginationState.pageSize,
      },
    },
    onColumnFiltersChange: (updater) => {
      const newFilters = typeof updater === 'function' ? updater(columnFilters) : updater
      setColumnFilters(newFilters)
      // Reset to first page when filters change
      if (paginationState.pageIndex !== 1) {
        setPaginationState({ ...paginationState, pageIndex: 1 })
      }
    },
    onSortingChange: setSorting,
    meta: {
      setSorting,
    },
  })

  const handleNextPage = () => {
    if (paginationMetadata?.next_page) {
      setPaginationState({
        ...paginationState,
        pageIndex: paginationMetadata.next_page,
      })
    }
  }

  const handlePreviousPage = () => {
    if (paginationMetadata?.prev_page) {
      setPaginationState({
        ...paginationState,
        pageIndex: paginationMetadata.prev_page,
      })
    }
  }

  return (
    <div className='flex flex-col gap-4'>
      <div className='grid grid-cols-4 gap-4'>
        <TextFilter<BrokersBrokerMember>
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
          property='id'
          placeholder='Filter by member ID'
        />
        <TextFilter<BrokersBrokerMember>
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
          property='email'
          placeholder='Filter by email address'
        />
        <TextFilter<BrokersBrokerMember>
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
          property='given_name'
          placeholder='Filter by first name'
        />
        <TextFilter<BrokersBrokerMember>
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
          property='family_name'
          placeholder='Filter by last name'
        />
        <TextFilter<BrokersBrokerMember>
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
          property='employer_name'
          placeholder='Filter by employer'
        />
        <TextFilter<BrokersBrokerMember>
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
          property='state'
          placeholder='Filter by state (e.g. CA)'
        />
        <TextFilter<BrokersBrokerMember>
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
          property='date_of_birth'
          placeholder='Filter by date of birth'
        />
      </div>

      <TanstackTable
        table={table}
        isLoading={isLoading}
        isError={isError}
        errorMessage='Failed to load members'
        noDataMessage='No members found'
        paginationLabelSingular='member'
        paginationMetadata={paginationMetadata}
        onNextPageClick={handleNextPage}
        onPreviousPageClick={handlePreviousPage}
        onRowClick={({ row }) => {
          const memberId = row.original.id
          assign(brokersMembers.show({ id: memberId }))
        }}
      />
    </div>
  )
}
